import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import AccessibilityToolbar from './AccessibilityToolbar';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../context/LanguageContext';
import '../styles/Navbar.css';

export default function Navbar({ user, setUser, setToken, theme, toggleTheme }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  const [showAccessibility, setShowAccessibility] = useState(false);

  // Notification bell state for all pages
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const bellRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!user || user.role === 'admin') {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/applicant/notifications', {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        params: {
          email: user.email || '',
          userId: user.id || ''
        }
      });
      if (res.data && res.data.notifications) {
        setNotifications(res.data.notifications);
        setUnreadCount(res.data.unreadCount || 0);
      }
    } catch (err) {
      // ignore
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
    const handleUpdated = () => fetchNotifications();
    window.addEventListener('notificationsUpdated', handleUpdated);
    const interval = setInterval(fetchNotifications, 15000);
    return () => {
      window.removeEventListener('notificationsUpdated', handleUpdated);
      clearInterval(interval);
    };
  }, [location.pathname, fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = async (notif) => {
    try {
      await axios.post(`/api/applicant/notifications/read/${notif.id}`);
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, isRead: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
      window.dispatchEvent(new Event('notificationsUpdated'));
    } catch (e) {
      console.error(e);
    }
    if (notif.applicantId) {
      localStorage.setItem('myApplicantId', notif.applicantId);
    }
    setShowNotifications(false);
    navigate('/dashboard');
  };

  const handleMarkAllRead = async () => {
    try {
      const unreadIds = notifications.filter(n => !n.isRead).map(n => n.id);
      await axios.post('/api/applicant/notifications/read-all', { ids: unreadIds });
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
      window.dispatchEvent(new Event('notificationsUpdated'));
    } catch (e) {
      console.error(e);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (setToken) setToken(null);
    if (setUser) setUser(null);
    navigate('/');
  };

  const isActive = (path) => {
    return location.pathname === path ? 'nav-item active' : 'nav-item';
  };

  return (
    <header className="university-header">
      {/* Yuqori rasmiy axborot paneli */}
      <div className="header-top-bar">
        <div className="container top-bar-content">
          <div className="official-emblem-text">
            <span>OʻZBEKISTON RESPUBLIKASI TAʼLIM VA ILM-FAN VAZIRLIGI</span>
          </div>
          <div className="top-bar-contacts">
            <span>📍 <a href="https://www.google.com/maps/search/?api=1&query=Turon+universiteti,+Qarshi+shahri,+Nasaf+ko%CA%BBchasi,+1/14-uy" target="_blank" rel="noreferrer" className="site-direct-link" title="Turon universitetini Google xaritada koʻrish">Qarshi sh., Nasaf koʻchasi, 1/14</a></span>
            <span className="divider-dot">•</span>
            <span>📞 <a href="tel:+998752200025">+998 (75) 220-00-25</a></span>
            <span className="divider-dot">•</span>
            <span><a href="mailto:turonuniversity@gmail.com">turonuniversity@gmail.com</a></span>
            <span className="divider-dot">•</span>
            <button
              type="button"
              className={`topbar-access-btn ${showAccessibility ? 'active' : ''}`}
              onClick={() => setShowAccessibility(!showAccessibility)}
              title="Koʻrish imkoniyati cheklanganlar uchun maxsus rejim"
            >
              👁️ Maxsus imkoniyatlar
            </button>
            <span className="divider-dot">•</span>
            <LanguageSwitcher />
          </div>
        </div>
      </div>

      {/* Ochiluvchi Maxsus imkoniyatlar paneli */}
      <AccessibilityToolbar isOpen={showAccessibility} onClose={() => setShowAccessibility(false)} />

      {/* Asosiy navigatsiya paneli */}
      <nav className="main-navbar">
        <div className="container nav-container">
          {/* Universitet rasmiy logotipi (Desktopdagi Yangi loyiha/logo.png) */}
          <div
            className="logo-brand"
            onClick={() => {
              if (user && user.role === 'admin') {
                navigate('/admin');
              } else {
                navigate('/');
              }
            }}
            title={t('INTERAKTIV XIZMATLAR PORTALI')}
          >
            <img src="/logo.png" alt="Turon University" className="turon-nav-logo-img" />
            <div className="brand-text">
              <span className="portal-sub">{t('INTERAKTIV XIZMATLAR PORTALI')}</span>
            </div>
          </div>

          {/* Markaziy qism: kiringanlar uchun ichki navigatsiya */}
          <div className="nav-center-section">

            {/* Faqat tizimga kirgan oddiy talabgorlarga koʻrinadi */}
            {user && user.role !== 'admin' && (
              <div className="nav-links">
                <button className={isActive('/register')} onClick={() => navigate('/register')}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="12" y1="18" x2="12" y2="12"></line><line x1="9" y1="15" x2="15" y2="15"></line></svg>
                  <span>{t('Ariza topshirish')}</span>
                </button>

                <button className={isActive('/dashboard')} onClick={() => navigate('/dashboard')}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                  <span>{t('Mening arizalarim')}</span>
                </button>
              </div>
            )}

          </div>

          {/* Oʻng tomon: kungi/tungi rejim, bildirishnomalar, foydalanuvchi profili yoki mehmonlar uchun tizimga kirish */}
          <div className="nav-actions">
            {/* Maxsus Kungi / Tungi rejimga oʻtkazuvchi tugma */}
            <button
              type="button"
              className="btn-theme-toggle"
              onClick={toggleTheme}
              title={theme === 'dark' ? t("Kunduzgi rejimga oʻtish (Light mode)") : t("Tungi rejimga oʻtish (Dark mode)")}
              aria-label={t("Kungi va tungi rejimni almashtirish")}
            >
              {theme === 'dark' ? (
                <span className="theme-toggle-inner">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="sun-icon">
                    <circle cx="12" cy="12" r="5"></circle>
                    <line x1="12" y1="1" x2="12" y2="3"></line>
                    <line x1="12" y1="21" x2="12" y2="23"></line>
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                    <line x1="1" y1="12" x2="3" y2="12"></line>
                    <line x1="21" y1="12" x2="23" y2="12"></line>
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                  </svg>
                  <span className="theme-mode-text">{t('Kunduzgi')}</span>
                </span>
              ) : (
                <span className="theme-toggle-inner">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="moon-icon">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                  </svg>
                  <span className="theme-mode-text">{t('Tungi')}</span>
                </span>
              )}
            </button>

            {/* Bildirishnomalar qoʻngʻiroqchasi (🔔) - faqat tizimga kirgan talabgorlarga koʻrinadi */}
            {Boolean(user && user.role !== 'admin') && (
              <div className="bell-notification-wrapper navbar-bell" ref={bellRef}>
                <button
                  type="button"
                  className={`btn-notification-bell ${unreadCount > 0 ? 'has-unread' : ''}`}
                  onClick={() => setShowNotifications(!showNotifications)}
                  title={t("Bildirishnomalar va yangiliklar")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                  </svg>
                  {unreadCount > 0 && (
                    <span className="bell-badge-counter">{unreadCount}</span>
                  )}
                </button>

                {showNotifications && (
                  <div className="notifications-dropdown-menu navbar-notif-dropdown">
                    <div className="notif-dropdown-header">
                      <div className="notif-header-title">
                        <span>🔔 {t('Bildirishnomalar')}</span>
                        {unreadCount > 0 && (
                          <span className="notif-unread-pill">{unreadCount} {t('ta yangi')}</span>
                        )}
                      </div>
                      {unreadCount > 0 && (
                        <button
                          type="button"
                          className="btn-mark-all-read"
                          onClick={handleMarkAllRead}
                        >
                          {t('Barchasini oʻqilgan qilish')}
                        </button>
                      )}
                    </div>

                    <div className="notif-list-container">
                      {notifications.length === 0 ? (
                        <div className="notif-empty-state">
                          <span>📭 {t('Hozircha bildirishnomalar mavjud emas')}</span>
                        </div>
                      ) : (
                        notifications.map(n => (
                          <div
                            key={n.id}
                            className={`notif-item-row ${n.isRead ? 'read' : 'unread'}`}
                            onClick={() => handleNotificationClick(n)}
                          >
                            <div className={`notif-item-icon ${n.type === 'passed' ? 'success' : n.type === 'edit_allowed' ? 'warning' : n.type === 'rejected' ? 'danger' : 'info'}`}>
                              {n.type === 'passed' ? '🏆' :
                               n.type === 'approved' ? '📅' :
                               n.type === 'edit_allowed' ? '✏️' :
                               n.type === 'rejected' ? '✕' : '📋'}
                            </div>
                            <div className="notif-item-body">
                              <div className="notif-item-title-row">
                                <span className="notif-item-title">{n.title}</span>
                                <span className="notif-item-time">{n.time}</span>
                              </div>
                              <p className="notif-item-msg">{n.message}</p>
                              {n.applicantId && (
                                <span className="notif-app-tag">{t('Ariza №')} {n.applicantId}</span>
                              )}
                            </div>
                            {!n.isRead && <span className="notif-unread-dot" />}
                          </div>
                        ))
                      )}
                    </div>

                    <div className="notif-dropdown-footer">
                      <button
                        type="button"
                        className="btn-notif-footer-link"
                        onClick={() => {
                          setShowNotifications(false);
                          navigate('/dashboard');
                        }}
                      >
                        📂 {t('Barcha arizalarim va monitoring')} →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {user ? (
              <div
                className="user-profile-badge"
                onClick={() => {
                  if (user && user.role === 'admin') navigate('/admin');
                }}
                style={{ cursor: user && user.role === 'admin' ? 'pointer' : 'default' }}
                title={user && user.role === 'admin' ? t('Admin boshqaruv paneliga oʻtish') : undefined}
              >
                <div className="avatar-circle">
                  {user.avatar ? (
                    <img src={user.avatar} alt={user.name || 'User'} className="user-avatar-img" />
                  ) : (
                    user.name ? user.name.charAt(0).toUpperCase() : 'U'
                  )}
                </div>
                <div className="user-meta">
                  <span className="user-display-name">{(user.name || user.email || '').replace(/\s*\(Ilmiy bo['ʻ]lim\)/gi, ' (Portal maʼmuriyati)')}</span>
                  <span className={`user-role-tag ${user.role}`}>
                    {user.role === 'admin' ? `🛡️ ${t('Portal administratori')}` : `🎓 ${t('Talabgor')}`}
                  </span>
                </div>
                <button
                  className="logout-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLogout();
                  }}
                  title={t("Tizimdan chiqish")}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
                </button>
              </div>
            ) : (
              <div className="guest-nav-group">
                <a href="tel:+998752200025" className="nav-hotline-pill" title={t("Turon universiteti aloqa markazi")}>
                  <div className="hotline-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                  </div>
                  <div className="hotline-details">
                    <span className="hotline-sub">{t('Call-markaz:')}</span>
                    <span className="hotline-num">+998 (75) 220-00-25</span>
                  </div>
                </a>

                <button className="btn-official-login" onClick={() => navigate('/login')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path><polyline points="10 17 15 12 10 7"></polyline><line x1="15" y1="12" x2="3" y2="12"></line></svg>
                  <span>{t('Tizimga kirish')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
}
